/* Lesepfad nach Rolle und die öffentliche Spieleransicht — reine Funktionen, kein DOM.
 *
 * Wer liest woher (Entscheidung Bastian, 2026-10-04):
 *   admin, board, data, staff  ->  Tabelle players (ganze Zeile samt Datenblock)
 *   guest                      ->  View players_public (nur öffentliche Felder)
 * Gäste (Berater, Agenten, Investoren) bekommen so nie Geburtsdatum, Pass, Dokumente,
 * Reisen oder Newsfeed zu sehen. Die Datenbank setzt es durch (players_select ohne
 * guest, supabase/migrations/…_players_select_umstellung.sql); diese Datei legt nur
 * fest, welchen Weg die App nimmt und wie die View zum Spielerobjekt wird.
 *
 * Die Grundspalten der View (20: 19 aus supabase/migrations/20261004000100_players_public.sql ohne player_card_pdf, dazu
 * transfermarkt_url aus 20261004000300_players_public_transfermarkt.sql und birth_date aus
 * 20261007000000_gaeste_geburtsdatum.sql — Entscheidung Bastian 2026-10-05: Gäste sehen das volle
 * Geburtsdatum). Die Spalte player_card_pdf (alte hochgeladene Karte) gibt es seit F5 (supabase/alte-karte-entfernen.sql)
 * nicht mehr; die App fragt sie nie ab und läuft mit der Ansicht vor und nach dem Skript. Alles andere fehlt am Spielerobjekt (docs, trips, …) und bleibt auf den
 * Vorgabewerten von ensurePlayerShape — jeder Lesecode verträgt das ohnehin
 * (Altdatensätze). `_publicOnly` kennzeichnet ein solches Objekt: Die Oberfläche blendet Teile aus,
 * die es für Gäste nicht gibt, und zeigt ohne Geburtsdatum (Spalte fehlt noch oder Datenblock leer)
 * nur das Geburtsjahr. Es wird nie gespeichert (stripTransient).
 */

import { ROLES, DEFAULT_ROLE } from './permissions.js';
import { ensurePlayerShape } from './playerShape.js';

export const PLAYERS_SOURCE_FULL = 'players';
export const PLAYERS_SOURCE_PUBLIC = 'players_public';
/* staff liest die Ansicht players_staff (supabase/staff-sichtbarkeit-1-ansichten.sql, Entscheidung Bastian
   2026-10-15): Stammdaten samt vollem Geburtsdatum, Material, Dokumentenstatus — ohne Reisen, Visa, Einladungen,
   Trial-Bewertungen, Ausweis-Scans, Passnummer, Passablauf und Geburtsort. */
export const PLAYERS_SOURCE_STAFF = 'players_staff';

/**
 * Aus welcher Quelle liest diese Rolle die Spieler? Unbekannte oder fehlende Rolle
 * fällt wie überall auf die engste Rolle (guest) zurück und liest damit die öffentliche
 * Ansicht — die sichere Richtung.
 *
 * @param {string|null|undefined} role
 * @returns {'players'|'players_public'|'players_staff'}
 */
export function playersReadSource(role) {
  const effective = ROLES.includes(role) ? role : DEFAULT_ROLE;
  if (effective === 'guest') return PLAYERS_SOURCE_PUBLIC;
  if (effective === 'staff') return PLAYERS_SOURCE_STAFF;
  return PLAYERS_SOURCE_FULL;
}

/** Die 19 Spalten der View vor birth_date. Rückfall der Abfrage, solange die Datenbank die neue Spalte nicht hat. */
export const PUBLIC_COLUMNS_LEGACY = [
  'id', 'first_name', 'last_name', 'status', 'pos', 'pos2', 'birth_year', 'avatar_path',
  'current_club', 'current_club_logo_path', 'national_team', 'national_team_level',
  'height', 'foot', 'nationality', 'highlight_video',
  'bio_photo_path', 'at_grassrunners_since', 'transfermarkt_url',
];

/**
 * Spalten der View (20), so wie die App sie liest. birth_date steht am Ende (Entscheidung Bastian,
 * 2026-10-05: Gäste sehen das volle Geburtsdatum; supabase/gaeste-geburtsdatum.sql).
 */
export const PUBLIC_COLUMNS = [...PUBLIC_COLUMNS_LEGACY, 'birth_date'];

/**
 * Die 22 Spalten mit age_group (Altersklasse, F1b; supabase/gaeste-altersklasse.sql). Das ist die Abfrage der App;
 * fehlt die Spalte noch in der Datenbank, fragt supabase/players.js ohne sie erneut an (PUBLIC_COLUMNS).
 */
export const PUBLIC_COLUMNS_WITH_AGE = [...PUBLIC_COLUMNS, 'age_group'];

/**
 * Die 24 Spalten mit match_videos (Match-Liste, nur title/url/opponent/shirtNumber/shirtColor/stats) und traits
 * (Schlagwörter) — Material-Reiter und Player Card für Gäste (F4, supabase/gaeste-material.sql). Das ist die Abfrage der
 * App; fehlen die Spalten noch in der Datenbank, fragt supabase/players.js mit den 22 Spalten (PUBLIC_COLUMNS_WITH_AGE) erneut an.
 */
export const PUBLIC_COLUMNS_WITH_MATERIAL = [...PUBLIC_COLUMNS_WITH_AGE, 'match_videos', 'traits'];

/** Meldet die Datenbank, dass match_videos oder traits fehlt (View noch nicht erweitert)? Postgres 42703 = undefined_column. */
export function isMissingMaterialColumn(error) {
  if (!error) return false;
  const text = String(error.message || '');
  return (error.code === '42703' || /does not exist/i.test(text)) && /match_videos|traits/.test(text);
}

/** Meldet die Datenbank, dass age_group fehlt (View noch nicht erweitert)? Postgres 42703 = undefined_column. */
export function isMissingAgeGroupColumn(error) {
  if (!error) return false;
  const text = String(error.message || '');
  return (error.code === '42703' || /does not exist/i.test(text)) && /age_group/.test(text);
}

/**
 * Meldet die Datenbank, dass birth_date fehlt (View noch nicht erweitert)? Dann fragt die App ohne die
 * Spalte erneut an, statt Gästen einen Lesefehler zu zeigen. Postgres-Code 42703 = undefined_column.
 */
export function isMissingBirthDateColumn(error) {
  if (!error) return false;
  const text = String(error.message || '');
  return (error.code === '42703' || /does not exist/i.test(text)) && /birth_date/.test(text);
}

/**
 * Zeile der View -> Spielerobjekt (über ensurePlayerShape, mit Vorgabewerten für alles,
 * was die View nicht liefert). Fehlende oder leere Werte werden zu den Vorgaben.
 * Nur Spalten der View werden übernommen — nichts anderes aus der Zeile.
 */
export function publicRowToPlayer(row) {
  const r = row || {};
  const player = ensurePlayerShape({
    id: r.id,
    firstName: r.first_name,
    lastName: r.last_name,
    status: r.status,
    pos: r.pos,
    pos2: r.pos2,
    jg: r.birth_year,
    avatarPath: r.avatar_path,
    currentClub: r.current_club,
    currentClubLogoPath: r.current_club_logo_path,
    nationalTeam: r.national_team,
    ntLevel: r.national_team_level,
    height: r.height,
    foot: r.foot,
    nationality: r.nationality,
    highlightVideo: r.highlight_video,
    bioPhotoPath: r.bio_photo_path,
    atGrassrunnersSince: r.at_grassrunners_since,
    dob: r.birth_date,
    transfermarktUrl: r.transfermarkt_url,
    /* Altersklasse: fehlt die Spalte oder ist sie leer, bleibt es leer (kein Chip) */
    ageGroup: r.age_group,
    /* Material für Gäste (F4): Matches und Schlagwörter; fehlt die Spalte, bleibt es leer (kein Abschnitt, keine Schlagwörter) */
    videos: r.match_videos,
    traits: r.traits,
  });
  player._publicOnly = true;
  return player;
}

/**
 * Zeile der Ansicht players_staff -> Spielerobjekt. Der Datenblock der Ansicht enthält nur die erlaubten
 * Schlüssel plus passportComplete und passportDaysLeft (abgeleitet, statt der gesperrten Passfelder).
 * `_staffView` kennzeichnet das Objekt: Es ist unvollständig und wird nie gespeichert (stripTransient,
 * savePlayer, usePlayers.save). Die Spalten gewinnen über den Datenblock, wie bei players.
 */
export function staffRowToPlayer(row) {
  const r = row || {};
  const d = r.data && typeof r.data === 'object' ? r.data : {};
  const player = ensurePlayerShape({
    ...d,
    id: r.id,
    currentClubId: r.current_club_id || null,
    firstName: r.first_name || d.firstName || '',
    lastName: r.last_name || d.lastName || '',
    status: r.status || d.status || 'trial',
  });
  player._staffView = true;
  return player;
}

/**
 * Welches Foto zeigt die große Fotoansicht (Player Info) bzw. die Liste? Das Akten-Foto
 * (bioPhotoPath, Altbestand) hat Vorrang, sonst das Spielerfoto (avatarPath). Bei Gästen
 * ist bioPhotoPath meist leer; dann gilt avatarPath — in der großen Ansicht als md, in
 * der Liste als thumb (domain/photoVariants.js variantForSize).
 */
export function photoPathFor(player) {
  const p = player || {};
  return p.bioPhotoPath || p.avatarPath || null;
}
